import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { HttpServiceResponse } from '@/services/HttpService/types';
import { RootState } from '@/store/types/RootState';
import { CreateCoverageRequest } from '../types/api/requests/CreateCoverageRequest';
import { LinkCoverageRequest } from '../types/api/requests/LinkCoverageRequest';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { AllClaimSetsSlots } from '@/domains/claimSets/enums/AllClaimSetsSlots';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import CoverageEvents from '@/domains/coverages/events';
import { FAILED_TO_SAVE } from '@/constants/texts';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import {
  createCoverage,
  deleteCoverage,
  getCoveragesFilteredList,
  getCoveragesListFailed,
  getCoveragesListSuccess,
  incrementCoveragesListPage,
  linkCoverageToClaim,
  unlinkCoverageFromClaim,
} from '../store/coveragesSlice';

function* createCoverageSaga({ payload }: PayloadAction<CreateCoverageRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const claimSetId = yield select(selectCurrentClaimSetId);

    const { data }: HttpServiceResponse<string> = yield call(HttpService.post, `/examiner/coverage/create-coverage`, {
      ...payload,
      claimSetId,
    });

    yield put(getCoveragesFilteredList());

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    EventService.dispatch(new CoverageEvents.CoverageCreated({}));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.AddCoverage);
  }
}

function* getCoveragesFilteredListSaga({ type }: PayloadAction<undefined>) {
  try {
    const pagination = yield select((state: RootState) => state.coverages.data.pagination);
    const savedFilters = yield select((state: RootState) => state.coverages.savedFiltersForSending);

    const requestUrl = `/filter/${EdsEntityCodes.Coverage}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: `${EdsEntityCodes.HelperTags}` })}`;

    const { data } = yield call(HttpService.post, requestUrl, savedFilters?.filters ?? [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const latestSavedFilters = yield select((state: RootState) => state.coverages.savedFiltersForSending);

    if (JSON.stringify(savedFilters) !== JSON.stringify(latestSavedFilters)) {
      return;
    }

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section: EdsDefinitionSections.SidePanel,
      includeId: true,
    });
    const transformedResponse = normalizedResponse.map((item) => ({ ...item, ...item.entities }));

    EdsDebugService.logResponse(EdsEntityCodes.Coverage, {
      section: EdsDefinitionSections.SidePanel,
      rawResponse: data,
      normalizedResponse: transformedResponse,
      dataBySlot: transformedResponse.length
        ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(transformedResponse[0])
        : {},
    });

    yield put(
      getCoveragesListSuccess({
        shouldRewrite: type !== incrementCoveragesListPage.type,
        data: {
          listings: transformedResponse as any,
          pagination: {
            ...data.pagination,
            isLastPage: pagination.page * pagination.perPage >= pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(getCoveragesListFailed());
  }
}

function* linkCoverageToClaimSaga({ payload }: PayloadAction<LinkCoverageRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const { data }: HttpServiceResponse<string> = yield call(
      HttpService.post,
      `/examiner/coverage/link-to-claim`,
      payload,
    );

    yield put(getCoveragesFilteredList());
    yield put(getBenefitSectionDetails(`${payload.claimId}`));

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.LinkCoverage);
  }
}

function* unlinkCoverageFromClaimSaga({ payload }: PayloadAction<LinkCoverageRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const { data }: HttpServiceResponse<string> = yield call(
      HttpService.post,
      `/examiner/coverage//unlink-from-claim`,
      payload,
    );

    yield put(getCoveragesFilteredList());
    yield put(getBenefitSectionDetails(`${payload.claimId}`));

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* deleteCoverageSaga({ payload: coverageId }: PayloadAction<number>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const { data }: HttpServiceResponse<string> = yield call(
      HttpService.delete,
      `/examiner/coverage?coverageId=${coverageId}`,
    );

    yield put(getCoveragesFilteredList());

    EventService.dispatch(new CoverageEvents.CoverageDeleted({}));

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

export function* coveragesWatcher() {
  yield takeLatest(createCoverage.type, createCoverageSaga);
  yield takeLatest([getCoveragesFilteredList.type, incrementCoveragesListPage.type], getCoveragesFilteredListSaga);
  yield takeLatest(linkCoverageToClaim.type, linkCoverageToClaimSaga);
  yield takeLatest(unlinkCoverageFromClaim.type, unlinkCoverageFromClaimSaga);
  yield takeLatest(deleteCoverage.type, deleteCoverageSaga);
}
